// src/utils/patientProfileCompleteness.js
//
// One definition of "this patient has told us enough to interpret their labs",
// used by the post-login route, the portal's profile gate, and the Health
// Profile form's own save validation.
//
// It lives here rather than in any of those three because they must agree. A
// gate that computes completeness differently from the form that satisfies it
// is a gate the patient cannot get out of.
//
// ── Why "None of these" exists ───────────────────────────────────────────────
//
// The Health Profile's conditions and family-history rows are chip
// multi-selects, and an empty row used to be ambiguous: it meant either "I have
// nothing to declare" or "I never answered this". Those need different
// treatment — the first is a complete answer from a healthy patient, the second
// is a gap — and no amount of reading the stored value can tell them apart.
//
// So each row now carries an explicit NONE_OF_THESE chip. Selecting it is an
// answer, which makes an untouched row unambiguously unanswered, and means a
// patient with no conditions is never held behind the gate for having nothing
// wrong with them.

export const NONE_OF_THESE = 'None of these';

/**
 * DOM ids for the two Settings sections the gate sends patients to.
 *
 * Here rather than inlined at either end because a link and its destination
 * have to agree on the string: the gate builds the URL, PatientSettingsPage
 * puts the id on the section, and a typo in one of them produces a link that
 * silently does nothing — it navigates, finds no element, and leaves the
 * patient at the top of the page exactly as before.
 */
export const HEALTH_PROFILE_ANCHOR = 'health-profile';
export const SYMPTOM_LOG_ANCHOR    = 'symptom-log';

/**
 * Which Settings section a patient should be dropped at, given what is missing.
 *
 * The health profile comes first when both are outstanding: it is four taps,
 * where the symptom log is twenty questions, so it is the one that makes the
 * page feel finishable rather than endless.
 */
export function firstIncompleteAnchor(status) {
  if (!status?.healthProfileComplete) return HEALTH_PROFILE_ANCHOR;
  if (!status?.symptomLogComplete)    return SYMPTOM_LOG_ANCHOR;
  return null;
}

/** Q1..Q20 — Previu_Symptom_Questionnaire_v1.0 (Constitution §VIII). */
export const TOTAL_SYMPTOM_QUESTIONS = 20;

/**
 * Questions SymptomLogSection only shows to female patients: Q19 (periods and
 * menopause) and Q20 (new lumps in breast, neck, armpit or groin).
 *
 * Exported so the gate and the questionnaire cannot disagree about how many
 * questions exist for a given patient. They did, and the consequence was a
 * lockout: the gate required all twenty, the questionnaire showed a male
 * patient eighteen, so he answered every question he was given, sat at
 * "18 of 20", and had no way to reach the other two — they were not on his
 * screen and never would be. A wall with no door.
 *
 * SymptomLogSection drives its own filter from this list, so adding a
 * sex-specific question in future updates both ends at once.
 */
export const WOMEN_ONLY_QUESTION_CODES = ['Q19', 'Q20'];

/**
 * How many questions THIS patient is actually asked.
 *
 * Not a constant, for the reason above. A patient can only be required to
 * complete what they are shown.
 *
 * Unknown or missing gender counts as not-female, which matches
 * SymptomLogSection's own `patient?.gender === 'FEMALE'` test — deliberately
 * the same direction, because the alternative is requiring answers to
 * questions the UI has decided not to ask.
 */
export function requiredSymptomCount(profile) {
  const isWoman = profile?.gender === 'FEMALE';
  return isWoman
    ? TOTAL_SYMPTOM_QUESTIONS
    : TOTAL_SYMPTOM_QUESTIONS - WOMEN_ONLY_QUESTION_CODES.length;
}

/**
 * Reverse-parses the two chip rows out of the stored medicalHistory string,
 * which looks like:
 *   "Conditions: Type 2 Diabetes, Hypertension. Family history: Stroke."
 *
 * Kept byte-compatible with HealthProfileForm's own parser — the form writes
 * this string and the gate reads it, so a difference between the two would
 * strand a patient who had just saved.
 */
export function parseHealthProfile(medicalHistory) {
  if (!medicalHistory) return { conditions: [], familyHistory: [] };
  try {
    const condMatch   = medicalHistory.match(/Conditions:\s*([^.]*)/i);
    const familyMatch = medicalHistory.match(/Family history:\s*([^.]*)/i);
    const split = (m) => (m ? m[1].split(',').map(s => s.trim()).filter(Boolean) : []);
    return { conditions: split(condMatch), familyHistory: split(familyMatch) };
  } catch {
    return { conditions: [], familyHistory: [] };
  }
}

/**
 * Both chip rows answered — each either naming conditions or explicitly saying
 * there are none.
 *
 * Medications and allergies are deliberately NOT required. They are labelled
 * optional on the form, and a gate that demands them would contradict its own
 * UI. They are also genuinely optional clinically: absence of a medication list
 * is not a missing answer in the way an unanswered conditions row is.
 *
 * @param {object} profile a patient response (needs .medicalHistory)
 */
export function isHealthProfileComplete(profile) {
  if (!profile) return false;
  const { conditions, familyHistory } = parseHealthProfile(profile.medicalHistory);
  return conditions.length > 0 && familyHistory.length > 0;
}

/**
 * Every symptom question THIS PATIENT is asked has been answered.
 *
 * The required total comes from requiredSymptomCount, not from
 * TOTAL_SYMPTOM_QUESTIONS. Comparing against the flat twenty is what locked
 * male patients out permanently: eighteen shown, eighteen answered, twenty
 * demanded.
 *
 * A partial questionnaire leaves the triage engine reading labs without the
 * symptom context those thresholds were calibrated against, which is what the
 * gate exists to prevent — but only for questions the patient was given.
 */
export function isSymptomLogComplete(answeredCount, profile) {
  return Number(answeredCount) >= requiredSymptomCount(profile);
}

/**
 * What the portal gate needs to know, in one object.
 *
 * `ready` is false while either input is still unknown, so the gate renders
 * nothing rather than flashing a freeze over a patient who has in fact
 * completed everything. Showing a returning patient a "you must fill this in"
 * wall for a few hundred milliseconds because a fetch had not landed is worse
 * than showing the wall a moment late.
 *
 * @param {?object} profile        patient response, or null while loading
 * @param {?number} answeredCount  symptom answers, or null while loading
 */
export function profileGateStatus(profile, answeredCount) {
  const ready = !!profile && answeredCount !== null && answeredCount !== undefined;
  const healthProfileComplete = isHealthProfileComplete(profile);
  const symptomLogComplete    = isSymptomLogComplete(answeredCount, profile);
  return {
    ready,
    healthProfileComplete,
    symptomLogComplete,
    complete: healthProfileComplete && symptomLogComplete,
    answeredCount: Number(answeredCount) || 0,
    // The count THIS patient is asked, so the gate's "x of y" matches the
    // questionnaire in front of them rather than a total they cannot reach.
    totalQuestions: requiredSymptomCount(profile),
  };
}
